/**
 * 张拓展（模拟）· 个人报告 JSON · ML（中素质 / 低绩效）待发展示例
 *
 * 与 v1.0 定稿版同源结构（见秦艳芳 mock 与李示例 mock 注释）：
 *   - 5 项素质维度：积极主动 / 敬业精神 / 卓越交付 / 坚韧抗压 / 先公后私（对齐 EvaluationScore 的 5 个 dimCode）
 *   - 5 名评价人：自评 / 直属领导 / 斜线·带教 / 下属 / 同级管理者（对齐 EvalRole 的 SELF / DIRECT / DIAGONAL / SUBORDINATE / PEER_LEADER）
 */

import type { PersonalReportSummaryJson } from './mockPersonalReportSummary';

const DIM_ROWS = [
  { code: 'INITIATIVE', name: '积极主动' },
  { code: 'DEDICATION', name: '敬业精神' },
  { code: 'DELIVERY', name: '卓越交付' },
  { code: 'RESILIENCE', name: '坚韧抗压' },
  { code: 'PUBLIC_FIRST', name: '先公后私' },
] as const;

type EvalRoleSlot = {
  roleLabel: string;
  evaluatorName: string;
  submitAt: string;
  scores: number[];
};

/** 5 维 × 5 评价人，张拓展 ML（中素质 / 低绩效） · 自评略乐观、跨视角偏审慎、下属反馈偏冷 */
const RAW_SCORES: EvalRoleSlot[] = [
  {
    roleLabel: '自评',
    evaluatorName: '张拓展（模拟）',
    submitAt: '2026-03-26T10:14:07.000Z',
    scores: [4.5, 4.8, 4.2, 4.6, 4.4],
  },
  {
    roleLabel: '直属领导',
    evaluatorName: '陈经理',
    submitAt: '2026-03-26T13:41:20.000Z',
    scores: [3.8, 4.1, 3.6, 4.0, 3.9],
  },
  {
    roleLabel: '斜线/带教',
    evaluatorName: '吴导师',
    submitAt: '2026-03-26T14:18:55.000Z',
    scores: [3.6, 3.9, 3.5, 3.8, 3.7],
  },
  {
    roleLabel: '下属',
    evaluatorName: '林同学',
    submitAt: '2026-03-26T14:42:11.000Z',
    scores: [3.4, 3.7, 3.3, 3.5, 3.5],
  },
  {
    roleLabel: '同级管理者',
    evaluatorName: '黄协作',
    submitAt: '2026-03-26T15:42:11.000Z',
    scores: [3.3, 3.6, 3.2, 3.4, 3.3],
  },
];

const COMMENTS_BY_ROLE: Record<string, Array<string | null>> = {
  自评: [
    '会主动看公司公告与季度目标，但向下分解到周计划落地仍偏少',
    '认同公司价值观与团队定位，但有时把"按时打卡"当作敬业的全部',
    '执行节奏中规中矩，偶有任务跨周延期；高难度项目时质量会先打折',
    '面对压力会调整情绪，但长周期紧张目标下偶尔回避难点',
    '愿意承担额外任务但优先级管理需提高，偶有把组里的资源用在自己 KPI',
  ],
  直属领导: [
    '主动性中性偏低，重要议题多为指令驱动，少有自下而上提目标',
    '敬业基本面在线，但对岗位之外的横向协作主动度不够',
    '执行闭环偶失节点，月报暴露根因分析空缺，质量稳定性需打磨',
    '抗压可，但遇到长周期硬骨头时会有撂挑子苗头',
    '先公后私意识中性，部分场景仍以小组业绩优先',
  ],
  '斜线/带教': [
    '主动性需要外部点火，约会议、推材料是被动发起',
    '敬业基本盘在，但深度投入度低，多数任务停在「完成」线',
    '交付节奏偶有延期，跨条线接口件常需要回邮件追问',
    '抗压一般，连续高压周次容易出现疲态',
    '先公后私倾向中性，跨条线场景里仍偏自己组的口径',
  ],
  下属: [
    '在团队里推动节奏的能力不强，大多数节奏是上级直接拉',
    '敬业但偏「随大流」，少给团队带来超额信号',
    '交付节奏稳定但不出彩，团队完成度被他自己的节奏拉到中位',
    '抗压一般，团队遇到不确定时先承压的不会是他',
    '会维护团队短期利益但少有跨部门为团队抢资源的动作',
  ],
  同级管理者: [
    '战略对齐弱，跨部门排期常被拉着走',
    '敬业基本盘有，但跨部门讨论时投入度不足',
    '跨部门交付中曾出现交接漏项，建议明确接口人',
    '抗压偶可，复杂跨部门议题上习惯先观望',
    '先公后私意识弱，资源协调时会出现明显的"自己组优先"',
  ],
};

const round2 = (n: number) => Math.round(n * 100) / 100;
const round1 = (n: number) => Math.round(n * 10) / 10;

const buildBreakdown = () =>
  RAW_SCORES.map((r) => {
    const rowTotal = round1(r.scores.reduce((a, b) => a + b, 0));
    const avgPerDim = round2(rowTotal / DIM_ROWS.length);
    return {
      roleLabel: r.roleLabel,
      evaluatorName: r.evaluatorName,
      submitAt: r.submitAt,
      rowTotal,
      avgPerDim,
      dimensions: DIM_ROWS.map((dr, i) => ({
        dimCode: dr.code,
        dimName: dr.name,
        score: r.scores[i],
        comment: COMMENTS_BY_ROLE[r.roleLabel]?.[i] ?? null,
      })),
    };
  });

const breakdown = buildBreakdown();
const rowTotals = breakdown.map((b) => b.rowTotal);
const maxTotal = Math.max(...rowTotals);
const minTotal = Math.min(...rowTotals);

const breakdownWithSummary = breakdown.map((b) => {
  let trend = '与各评价人行合计相近';
  if (b.rowTotal === maxTotal) trend = '相对其他角色，本条行合计为最高的一档';
  else if (b.rowTotal === minTotal) trend = '相对其他角色，本条行合计为最低的一档';
  const summaryLine = `${b.roleLabel}「${b.evaluatorName}」打分覆盖 ${DIM_ROWS.length} 项素质维度，行合计 ${b.rowTotal.toFixed(1)}，折算单维均值约 ${b.avgPerDim.toFixed(2)} 分（标尺 0～7）。${trend}。以下为其各维度分值与评语。`;
  return { ...b, summaryLine };
});

const radar = DIM_ROWS.map((dr, i) => {
  const all = RAW_SCORES.map((r) => r.scores[i]);
  return {
    code: dr.code,
    label: dr.name,
    avg: round2(all.reduce((a, b) => a + b, 0) / all.length),
  };
});

const SELF_IDX = 0;
const DIRECT_IDX = 1;
const dimensionComparison = DIM_ROWS.map((dr, i) => {
  const dimName = dr.name;
  const selfScore = RAW_SCORES[SELF_IDX].scores[i];
  const directScore = RAW_SCORES[DIRECT_IDX].scores[i];
  const nonSelf = RAW_SCORES.filter((_, idx) => idx !== SELF_IDX).map((r) => r.scores[i]);
  const othersAvg = round2(nonSelf.reduce((a, b) => a + b, 0) / nonSelf.length);
  const spread = round2(selfScore - othersAvg);
  let note = '自评与非自评接近';
  if (spread > 0.5) note = '自评高于非自评均值（自信偏高）';
  else if (spread < -0.5) note = '自评低于非自评均值（自我谦抑）';
  if (spread >= 1) note = '自评明显高于非自评均值（建议关注校准）';
  return {
    dimCode: dr.code,
    dimName,
    selfScore,
    directScore,
    othersAvg,
    spread,
    note,
  };
});

const insightsQualitative = [
  `各评价人素质项行合计极差约 ${round1(maxTotal - minTotal)} 分（最高 ${maxTotal.toFixed(1)} / 最低 ${minTotal.toFixed(1)}）；自评显著高于跨部门视角，建议在「积极主动 / 卓越交付」维度增加共识对齐与事实证据。`,
  `直属与斜线带教均认为「卓越交付」是相对短板（非自评均值约 ${dimensionComparison[2].othersAvg?.toFixed(2)}），可作为下半年改进重点；下属反馈整体偏冷，是这张画像里需要追问的"下拉力"。`,
  `先公后私维度上同级管理者打分偏低，跨条线资源协调的"自己组优先"倾向是横向口碑的主要阻力，建议绑定 1 个跨部门项目刻意练习。`,
];

export const MOCK_PERSONAL_REPORT_SUMMARY_GROWTH: PersonalReportSummaryJson = {
  emp: {
    id: 0,
    name: '张拓展（模拟）',
    hireDate: '2022-03-01',
    deptPath: '广西分公司 / 营销获客端 / 私域电商组',
    position: '私域电商运营',
    nineBoxResults: [
      {
        qualityStandardScore: 2.85,
        qualityLevel: 'M',
        performanceLevel: 'L',
        finalCell: 'ML',
      },
    ],
    competencyAssessments: [
      {
        weightedScore: 3.1,
        evaluator: { name: '陈经理' },
        status: 'SUBMITTED',
        submitAt: '2026-03-26T16:02:00.000Z',
        redLines: null,
      },
    ],
    highPotentialAsEvaluatee: [{ resultLabel: '观察池（建议匹配 1 个跨部门小项目历练）' }],
  },
  radar,
  tasks: RAW_SCORES.length,
  insightsQualitative,
  dimensionComparison,
  evaluationBreakdown: breakdownWithSummary,
  qualityAnalysisMeta: {
    advantageMeanThreshold: 5.5,
    concernMeanThreshold: 4,
    desc:
      '报告中「优势 / 常规 / 短板」：仅依据本期各素质维度上所有已提交 360° 评价的算术均分，与阈值比较——均分 ≥ 5.5 标为「优势」，< 4 标为「短板」，介于其间为「常规」。该标签与高阶九宫格的标准分档位可能同源不同算，请以制度为准。',
  },
  competencyRadar: [
    { axis: '专业深度', score: 3.0 },
    { axis: '业务影响', score: 2.8 },
    { axis: '带教协同', score: 3.4 },
    { axis: '结果导向', score: 3.1 },
    { axis: '文化践行', score: 3.6 },
  ],
  competencyAssessment: {
    weightedScore: 3.1,
    evaluatorName: '陈经理',
    status: 'SUBMITTED',
    submitAt: '2026-03-26T16:02:00.000Z',
    redLines: null,
  },
  performanceScore: {
    score: 71,
    level: 'L',
    source: '月报汇总',
  },
  leaderComment:
    '张拓展本期表现为稳健贡献型骨干，短板集中在「卓越交付」与跨部门可见度；建议下一季度绑定 1 名导师共创里程碑复盘，并用一次跨部门牵头交付补齐对外协作标签。',
  aiNarrative: {
    profile:
      '张拓展当前处于需要用关键交付重新建立成长斜率的阶段。自评略高于多源他评，跨部门与下属视角均偏审慎；短期应减少宽泛提升目标，集中完成一个可被验证的突破。',
    suggestions:
      '1. 选择一个 90 天内可闭环的跨部门项目，与导师共同拆出周度里程碑。\n2. 每月收集至少两条交付事实，校准自评与他评之间的偏差。\n3. 优先提升卓越交付与协作可见度，再进入更大范围的岗位历练。',
  },
  aiEnabled: true,
  aiQueueStatus: 'READY',
};
